import { useEffect } from 'react';
import useTripStore from '../store/useTripStore';
import { useDay, useHotel } from './useTripStoreSelectors';
import { useTripDesignerConfig } from '../config/TripDesignerConfigProvider';
import { reconcileRoomSelection } from '../logic/reconcileRoomSelection';

interface RoomsAvailabilityData {
  requestId?: string;
  expiresAt?: string;
  possibilities?: any[];
}

// KD-3594 D4 — a rooms-availability requestId expires ~20 min after it is
// issued; useHotelRoomAvailability's `refetchInterval` gets a fresh one
// before that happens. This hook is what reacts to it: reconcile the day's
// current room selection against the new possibilities and update it in
// place, silently unless something actually changed.
//
// Mounted once per hotel day. `roomsData` comes from that same day's own
// `useHotelRoomAvailability` call, which shares one cached query per hotel
// group (react-query dedupes by query key) — so every day in a group sees
// the same `requestId`, but only the day holding `day.selectedRoom` (the
// group's first day) ever has anything to reconcile; every other day is a
// no-op by construction.
export function useRoomSelectionRefresh(
  dayIndex: number,
  roomsData: RoomsAvailabilityData | undefined,
) {
  const day = useDay(dayIndex);
  const hotel = useHotel(dayIndex);
  const updateDay = useTripStore((state) => state.updateDay);
  const { onEvent } = useTripDesignerConfig();

  const selection = day?.selectedRoom;
  const requestId = roomsData?.requestId;

  const hasSelection = !!selection;
  useEffect(() => {
    if (!requestId || !selection || requestId === selection.requestId) {
      return;
    }

    const previousTotalPrice = day?.hotelPricing?.price;
    const { status, rooms, roomKeys, totalPrice } = reconcileRoomSelection(
      selection,
      roomsData?.possibilities ?? [],
      previousTotalPrice,
    );
    const hotelId = day?.hotelId;
    const hotelName = hotel?.name ?? hotel?.displayName;

    if (status === 'same') {
      updateDay(dayIndex, {
        selectedRoom: {
          ...selection,
          requestId,
          expiresAt: roomsData?.expiresAt,
          availabilityData: roomsData?.possibilities,
        },
      });
      return;
    }

    if (status === 'priceChanged') {
      updateDay(dayIndex, {
        selectedRoom: {
          ...selection,
          allRooms: rooms,
          roomKey: roomKeys,
          requestId,
          expiresAt: roomsData?.expiresAt,
          availabilityData: roomsData?.possibilities,
        },
        hotelPricing: { price: totalPrice, currency: hotel?.currency },
      });
      onEvent?.('rooms.priceChanged', {
        hotelId,
        hotelName,
        previousPrice: previousTotalPrice,
        price: totalPrice,
        currency: hotel?.currency,
      });
      return;
    }

    // status === 'unavailable'
    updateDay(dayIndex, {
      hotelPricing: { price: NaN, currency: hotel?.currency },
      selectedRoom: {
        roomKey: roomKeys,
        roomData: { name: rooms[0]?.name },
        allRooms: rooms,
        isUnavailable: true,
      },
    });
    onEvent?.('rooms.unavailable', { hotelId, hotelName });
    // `selection`/`roomsData` are read fresh from the closure on every call;
    // only their identifying fields below decide whether the effect re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    requestId,
    selection?.requestId,
    hasSelection,
    roomsData?.possibilities,
    roomsData?.expiresAt,
    dayIndex,
    day?.hotelId,
    day?.hotelPricing?.price,
    hotel?.name,
    hotel?.displayName,
    hotel?.currency,
    updateDay,
    onEvent,
  ]);
}
